import { DecoratorNode, EditorConfig, LexicalEditor, LexicalNode, SerializedLexicalNode, Spread, createEditor } from "lexical";
import React, { Suspense } from "react";

import { NestedEditorMixin, NestedEditorMixinPayload, NestedEditorPayload, SerializedNestedEditor } from "../mixins/NestedEditorMixin";
import { StickyMixin, StickyMixinPayload, StickyPayload, SerializedSticky } from "../mixins/StickyMixin";
import { ContainerMixin, ContainerMixinPayload, ContainerPayload, SerializedContainer } from "../mixins/ContainerMixin";

const StickyComponent = React.lazy(() => import('../mixins/components/StickyComponent'))
const StickyEditorComponent = React.lazy(() => import('./StickyEditorComponent'))

export interface StickyEditorPayload extends StickyPayload, ContainerPayload, NestedEditorPayload {
  key?: string;
}

export type SerializedStickyEditorNode = Spread<
  SerializedSticky & SerializedContainer & SerializedNestedEditor,
  SerializedLexicalNode
>;

class StickyEditorBase extends DecoratorNode<JSX.Element> implements StickyMixinPayload, ContainerMixinPayload, NestedEditorMixinPayload {
  __x: number;
  __y: number;
  __z: number | 'auto';
  __width: number;
  __height: number;
  __nestedEditor: LexicalEditor;

  constructor(
    x?: number,
    y?: number,
    z?: number | 'auto',
    width?: number,
    height?: number,
    nestedEditor?: LexicalEditor,
    key?: string,
  ) {
    super(key);
    this.__x = x || 15;
    this.__y = y || 30;
    this.__z = z || 'auto';
    this.__width = width || 200;
    this.__height = height || 100;
    this.__nestedEditor = nestedEditor || createEditor();
  }

  createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
    const element = document.createElement('div');
    element.className = 'sticky-editor';
    return element;
  }

  updateDOM(prevNode: LexicalNode, dom: HTMLElement, config: EditorConfig) {
    return false;
  }

  exportJSON(): SerializedStickyEditorNode {
    const self = this.getLatest();
    return {
      type: StickyEditorNode.getType(),
      version: 1,
      x: self.__x,
      y: self.__y,
      z: self.__z,
      width: self.__width,
      height: self.__height,
      nestedEditor: self.__nestedEditor.toJSON(),
    }
  }
}

export class StickyEditorNode extends StickyMixin(ContainerMixin(NestedEditorMixin(StickyEditorBase))) {
  static getType() {
    return "sticky-editor";
  }

  static clone(node: StickyEditorNode) {
    return new StickyEditorNode(node.__x, node.__y, node.__z, node.__width, node.__height, node.__nestedEditor, node.__key);
  }

  static importJSON(json: SerializedStickyEditorNode) {
    const newEditor = new StickyEditorNode(
      json.x || 0,
      json.y || 0,
      json.z || 'auto',
      json.width || 0,
      json.height || 0,
    );
    const editor = json.nestedEditor;
    const nestedEditor = newEditor.__nestedEditor;
    const editorState = nestedEditor.parseEditorState(editor.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return newEditor;
  }

  updateDOM(prevNode: LexicalNode, dom: HTMLElement, config: EditorConfig) {
    super.updateDOM(prevNode, dom, config);
    return false;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <StickyComponent
          nodeKey={this.__key}
          x={this.__x}
          y={this.__y}
          z={this.__z}
          width={this.__width}
          height={this.__height}
        >
          <StickyEditorComponent
            nestedEditor={this.__nestedEditor}
            parentNodeKey={this.__key}
          />
        </StickyComponent>
      </Suspense>
    )
  }
}

export const $createStickyEditorNode = (
  payload: StickyEditorPayload,
) => {
  const {
    x,
    y,
    z,
    width,
    height,
    nestedEditor,
    key,
  } = payload;
  return new StickyEditorNode(x, y, z, width, height, nestedEditor, key);
}

export const $isStickyEditorNode = (node: unknown): node is StickyEditorNode => {
  return node instanceof StickyEditorNode;
}